import { useLogout } from '@rahtab/auth/lib/token-based-auth';
import { StackScreenProps } from '@rahtab/stack-navigator';
import { Screen } from '@rahtab/system';
import { withNumbers } from '@rahtab/system/lib/tools/string';
import {
  BottomSheet,
  Button,
  Card,
  CardCaption,
  CardContainer,
  getSpacing,
  HorizontalLine,
  ImageButton,
  ItemCard,
  Tag,
  Text,
  TextInput,
  TextView,
  ToggleButton,
} from '@rahtab/ui';
import HistoryIcon from '@rahtab/ui/dist/icons/HistoryIcon';
import SignoutIcon from '@rahtab/ui/dist/icons/SignoutIcon';
import SupportIcon from '@rahtab/ui/dist/icons/SupportIcon';
import RahtabLogo from '@rahtab/ui/dist/logos/RahtabEnglishLogotype';
import type { RootStackType } from 'components/Router';
import useVetInfo from 'hooks/useVetInfo';
import React, { ReactElement, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';

type HomeScreenTitleType = 'VerifiedLoadsHistory' | 'Support' | 'SignOut';

interface HomeScreenButton {
  enTitle: HomeScreenTitleType;
  faTitle: string;
  svg?: ReactElement;
  link?: keyof RootStackType;
}

const homeScreenButtons: HomeScreenButton[] = [
  {
    enTitle: 'VerifiedLoadsHistory',
    faTitle: 'سوابق بار',
    svg: <HistoryIcon />,
    link: 'VerifiedLoadsHistory',
  },
  {
    enTitle: 'Support',
    faTitle: 'پشتیبانی',
    svg: <SupportIcon />,
    link: 'Support',
  },
  {
    enTitle: 'SignOut',
    faTitle: 'خروج',
    svg: <SignoutIcon />,
  },
];

type HomeProps = StackScreenProps<RootStackType, 'Home'>;

const Home: React.VFC<HomeProps> = () => {
  const [orderType, setOrderType] = useState<'مرسوله' | 'سفارش'>('سفارش');
  const [orderCode, setOrderCode] = useState('');
  const [providerSearchType, setProviderSearchType] = useState<'شناسه' | 'کد ملی' | 'مشخصات'>('شناسه');
  const [providerSearchValue, setProviderSearchValue] = useState('');
  const [signoutBtmSheet, setSignoutBtmSheet] = useState(false);
  const { loading, data } = useVetInfo();
  const { logout, logoutLoading } = useLogout();
  return (
    <>
      <BottomSheet visible={signoutBtmSheet} onRequestClose={() => setSignoutBtmSheet(false)}>
        <View style={{ alignItems: 'center', marginBottom: getSpacing() }}>
          <SignoutIcon width={100} height={100} style={{ marginBottom: getSpacing() }} />
          <Text variant="title" color="primary">
            خروج از برنامه
          </Text>
        </View>
        <Text>آیا برای خروج از برنامه اطمینان دارید؟</Text>
        <View style={styles.rowView}>
          <Button
            color="primary"
            variant="outlined"
            text="بازگشت"
            style={{ marginTop: getSpacing(), flex: 1 }}
            onPress={() => setSignoutBtmSheet(false)}
          />
          <Button
            color="primary"
            text="خروج"
            style={{ marginTop: getSpacing(), flex: 1.5, marginLeft: getSpacing() }}
            onPress={() => {
              setSignoutBtmSheet(false);
              logout();
            }}
          />
        </View>
      </BottomSheet>
      <Screen
        data={data}
        loading={loading}
        transparentLoading={logoutLoading}
        renderHeader={() => {
          return <RahtabLogo style={{ alignSelf: 'flex-end' }} />;
        }}
        renderControls={(info) => {
          return (
            <ItemCard
              title={info.fullName}
              hasChevron={false}
              titleMarginBottom="dense"
              inHeader={true}
              image={info.imageUrl}>
              <View style={StyleSheet.flatten([styles.rowView, { justifyContent: 'space-between' }])}>
                <Text color="description" margin="dense">
                  شناسه: <Text margin="dense">{info.identification}</Text>
                </Text>
              </View>
              <Text color="description" margin="none">
                واحد: <Text margin="none">{info.supplierName}</Text>
              </Text>
            </ItemCard>
          );
        }}
        renderMain={(info) => {
          const { loadInquiryAccess, providerInquiryAccess } = info;
          const isPublicVet = info.vetType === 2;
          const nav = homeScreenButtons.filter(({ enTitle }) => {
            if (enTitle !== 'VerifiedLoadsHistory') return true;
            if (!isPublicVet) return true;
            return false;
          });
          const getSearchProviderPlaceholder = () => {
            if (providerSearchType === 'شناسه') {
              return 'شناسه پذیرنده را وارد نمایید.';
            } else if (providerSearchType === 'کد ملی') {
              return 'کد ملی نماینده پذیرنده را وارد نمایید.';
            } else {
              return 'نام فروشگاه و یا نماینده را وارد نمایید.';
            }
          };
          return (
            <FlatList
              ListHeaderComponentStyle={styles.contentContainerStyle}
              keyboardShouldPersistTaps="handled"
              ListHeaderComponent={
                <>
                  {loadInquiryAccess ? (
                    <Card>
                      <CardCaption style={styles.rowView}>
                        <Text margin="none" color="primary" variant="cardTitle">
                          {isPublicVet ? 'استعلام شناسه اعلامی' : 'استعلام شناسه بار ورودی'}
                        </Text>
                      </CardCaption>
                      <CardContainer>
                        <ToggleButton
                          color="primary"
                          selected={orderType}
                          onPress={(type) => {
                            setOrderCode('');
                            setOrderType(type as typeof orderType);
                          }}>
                          <ToggleButton.Item label="سفارش" />
                          <ToggleButton.Item label="مرسوله" />
                        </ToggleButton>

                        <Text style={{ textAlign: 'justify' }}>
                          {isPublicVet
                            ? `شناسه ${orderType} اعلامی مرغدار را وارد نمایید.`
                            : `شناسه ${orderType} ثبت شده در مجوز حمل دامپزشکی را وارد نمایید.`}
                        </Text>
                        <View style={styles.rowView}>
                          <TextInput
                            value={orderCode}
                            keyboardType="numeric"
                            onChangeText={withNumbers(setOrderCode)}
                            placeholder={`شناسه ${orderType} را وارد نمایید.`}
                            style={{ flex: 1 }}
                          />
                          <Text> - </Text>
                          <TextView value={orderType === 'مرسوله' ? 'RNSO' : 'RNPO'} style={styles.textView} />
                        </View>
                        <View style={{ padding: 3 }}>
                          {orderType === 'مرسوله' ? (
                            <Button
                              color="primary"
                              text="استعلام شناسه مرسوله"
                              disabled={!orderCode}
                              screenName={isPublicVet ? 'SubmitShipmentGenericVet' : 'SubmitConsigmentInfo'}
                              params={{ RequestId: `RNSO-${orderCode}` }}
                            />
                          ) : null}
                          {orderType === 'سفارش' ? (
                            <Button
                              color="primary"
                              text="استعلام شناسه سفارش"
                              disabled={!orderCode}
                              screenName={isPublicVet ? 'LoadInquiry' : 'SubmitLoadInfo'}
                              params={{ RequestId: `RNPO-${orderCode}` }}
                            />
                          ) : null}
                        </View>
                        {!isPublicVet && (
                          <>
                            <HorizontalLine style={styles.separator} />
                            <Button color="secondary" text="ثبت بار ورودی بدون شناسه" screenName="SubmitLoadInfo" />
                          </>
                        )}
                      </CardContainer>
                    </Card>
                  ) : null}
                  {providerInquiryAccess ? (
                    <Card style={{ marginTop: getSpacing() }}>
                      <CardCaption style={styles.rowView}>
                        <Text margin="none" color="primary" variant="cardTitle">
                          استعلام اطلاعات فروشگاه پذیرنده
                        </Text>
                      </CardCaption>
                      <CardContainer>
                        <ToggleButton
                          color="primary"
                          selected={providerSearchType}
                          onPress={(type) => {
                            setProviderSearchValue('');
                            setProviderSearchType(type as typeof providerSearchType);
                          }}>
                          <ToggleButton.Item label="شناسه" />
                          <ToggleButton.Item label="کد ملی" />
                          <ToggleButton.Item label="مشخصات" />
                        </ToggleButton>

                        <Text style={{ textAlign: 'justify' }}>
                          {providerSearchType === 'شناسه'
                            ? 'شناسه فروشگاه مورد نظر خود که باید بر روی نشـــان پذیرنـدگی مندرج بر روی درب ورودی نصب باشد، را وارد نمایید.'
                            : null}
                          {providerSearchType === 'کد ملی'
                            ? 'شناسه ملی نماینده پذیرنده (صاحب پروانه واحد صنفی خرده فروشی) را وارد نمایید.'
                            : null}
                          {providerSearchType === 'مشخصات'
                            ? 'مشخصات فروشگاه مورد نظر خود که باید بر روی نشـــان پذیرنـدگی مندرج بر روی درب ورودی نصب باشد، را وارد نمایید.'
                            : null}
                        </Text>
                        <View style={styles.rowView}>
                          <TextInput
                            value={providerSearchValue}
                            keyboardType={providerSearchType !== 'مشخصات' ? 'numeric' : 'default'}
                            onChangeText={withNumbers(setProviderSearchValue)}
                            placeholder={getSearchProviderPlaceholder()}
                            style={{ flex: 1 }}
                          />
                          {providerSearchType === 'شناسه' ? (
                            <>
                              <Text> - </Text>
                              <TextView value="RNS" style={{ width: 50 }} />
                            </>
                          ) : null}
                        </View>
                        <View style={{ padding: 3 }}>
                          <Button
                            color="primary"
                            text="استعلام اطلاعات پذیرنده"
                            disabled={!providerSearchValue}
                            screenName="SearchProvider"
                            params={{
                              type: providerSearchType,
                              value:
                                providerSearchType === 'شناسه' ? `RNS-${providerSearchValue}` : providerSearchValue,
                            }}
                          />
                        </View>
                      </CardContainer>
                    </Card>
                  ) : null}
                </>
              }
              keyExtractor={(item) => `homeScreenButton-${item.enTitle}`}
              data={nav}
              contentContainerStyle={styles.contentContainerStyle}
              numColumns={3}
              renderItem={({ item }) => {
                return (
                  <ImageButton
                    screenName={item.link}
                    svg={item.svg}
                    name={item.faTitle}
                    onPress={item.enTitle === 'SignOut' ? () => setSignoutBtmSheet(true) : undefined}
                  />
                );
              }}
            />
          );
        }}
      />
    </>
  );
};

export default Home;

const styles = StyleSheet.create({
  card: {
    margin: getSpacing(),
  },
  contentContainerStyle: {
    padding: getSpacing(0.5),
  },
  rowView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardIcon: {
    marginRight: getSpacing(0.6),
    marginTop: getSpacing(0.3),
  },
  textView: {
    width: 58,
  },
  separator: {
    marginTop: getSpacing(),
  },
});
